import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import type { RootState } from '../../../store';
import { formatDateToDdmmyyyy } from '../../../utils/format';
import { fetchNetwork } from '../../../store/slices/usersSlice';
import Pagination from '../../common/Pagination';
import TableSkeleton from '../../common/TableSkeleton';
import { Users, Search, X, ChevronDown, ChevronUp, ChevronsUpDown, Eye, EyeOff, Copy, Check, RefreshCw } from 'lucide-react';
import DateRangePicker from '../../common/DateRangePicker';
import StatCard, { StatCardRow } from '../../common/StatCard';
import Select from '../../common/Select';
import ActiveStatusBadge from '../../common/ActiveStatusBadge';


const NetworkTab: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { network } = useAppSelector((state: RootState) => state.users);
    const { stats, users = [], loading } = network || { stats: null, users: [], loading: false, error: null };

    // URL Search Params for Pagination
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const itemsPerPage = 15;

    // Filter State
    const [role, setRole] = useState<string>('');
    const [search, setSearch] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    // Debounce Search (by mobile or name)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Memoize params
    const fetchParams = useMemo(() => ({
        page: currentPage,
        limit: itemsPerPage,
        role: role || undefined,
        search: debouncedSearch || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
    }), [currentPage, role, debouncedSearch, fromDate, toDate]);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    const handleSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedUsers = useMemo(() => {
        let sortableItems = [...users];
        if (sortConfig !== null) {
            sortableItems.sort((a: any, b: any) => {
                let aVal: any = 0;
                let bVal: any = 0;
                if (sortConfig.key === 'referrals') {
                    aVal = a.total_referrals || 0;
                    bVal = b.total_referrals || 0;
                } else if (sortConfig.key === 'units') {
                    aVal = a.purchased_units || 0;
                    bVal = b.purchased_units || 0;
                } else if (sortConfig.key === 'coinsEarned') {
                    aVal = a.earned_coins || 0;
                    bVal = b.earned_coins || 0;
                } else if (sortConfig.key === 'joinedDate') {
                    aVal = new Date(a.created_at || 0).getTime();
                    bVal = new Date(b.created_at || 0).getTime();
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [users, sortConfig]);

    const [copiedField, setCopiedField] = useState<string | null>(null);
    const handleCopy = (text: string, key: string, e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopiedField(key);
        setTimeout(() => setCopiedField(null), 1500);
    };

    const [revealedFields, setRevealedFields] = useState<Set<string>>(new Set());
    const toggleReveal = (key: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setRevealedFields(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };
    const maskMobile = (mobile: string) => `+91 ••••••${String(mobile).slice(-4)}`;

    const SortIcon = ({ sortKey }: { sortKey: string }) => {
        if (sortConfig?.key === sortKey) {
            return sortConfig.direction === 'asc' ? <ChevronUp size={14} className="text-slate-600" /> : <ChevronDown size={14} className="text-slate-600" />;
        }
        return <ChevronsUpDown size={14} className="text-slate-300 group-hover:text-slate-400" />;
    };

    // Fetch Data
    useEffect(() => {
        dispatch(fetchNetwork(fetchParams));
    }, [dispatch, fetchParams]);

    const setCurrentPage = useCallback((page: number) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('page', String(page));
            return newParams;
        });
    }, [setSearchParams]);


    // Calculate total pages
    // Use stats.page, stats.limit, stats.user_count if available
    const totalPages = stats ? Math.ceil(stats.user_count / stats.limit) : 0;

    return (
        <div className="w-full">
            <div>
                {/* Header & Stats Cards */}
                <div className="flex flex-col gap-4 mb-3">
                    <div className="flex flex-wrap items-center justify-between gap-3 w-full relative z-20">
                        {/* Search Input on the Left */}
                        <div className="relative w-full sm:w-auto sm:min-w-[280px]">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                            <input
                                type="text"
                                placeholder="Search by Mobile or Name"
                                className="h-[42px] pl-10 pr-8 text-sm border-0 shadow-sm rounded-xl bg-white text-slate-700 outline-none focus:border-[#1B4332] transition-all duration-200 w-full"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 font-bold bg-transparent border-none cursor-pointer"
                                    type="button"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        {/* Remaining Filters on the Right */}
                        <div className="flex flex-wrap items-center gap-3 sm:ml-auto">
                            {/* Date Range Picker */}
                            <DateRangePicker
                                className="w-full sm:w-[240px]"
                                buttonClassName="rounded-xl w-full"
                                startDate={fromDate}
                                endDate={toDate}
                                onChange={(start, end) => {
                                    setFromDate(start);
                                    setToDate(end);
                                    setCurrentPage(1);
                                }}
                                onClear={() => {
                                    setFromDate('');
                                    setToDate('');
                                    setCurrentPage(1);
                                }}
                            />

                            {/* Role Select */}
                            <Select
                                value={role}
                                onChange={(v) => { setRole(v); setCurrentPage(1); }}
                                options={[
                                    { value: '', label: 'All Roles' },
                                    { value: 'Investor', label: 'Investor' },
                                    { value: 'Employee', label: 'Employee' },
                                    { value: 'Supervisor', label: 'Supervisor' },
                                    { value: 'Cashier', label: 'Cashier' },
                                    { value: 'SpecialCategory', label: 'Special Category' },
                                    { value: 'Admin', label: 'Admin' },
                                    { value: 'SuperAdmin', label: 'Super Admin' },
                                ]}
                                className="h-[42px] pl-4 pr-3 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-700 font-medium"
                            />

                            {/* Refresh Button */}
                            <button
                                type="button"
                                onClick={() => dispatch(fetchNetwork(fetchParams))}
                                disabled={loading}
                                title="Refresh"
                                className="flex items-center justify-center gap-2 h-[42px] px-4 shrink-0 border border-slate-200 shadow-sm rounded-xl bg-white text-black outline-none hover:bg-slate-50 focus:border-[#1B4332] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                            >
                                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                                Refresh
                            </button>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    {stats && (
                        <StatCardRow>
                            <StatCard title="Total Users" value={String(stats.user_count)} />
                            <StatCard title="Distributed Coins" value={String(stats.total_distributed_coins?.toLocaleString('en-IN') || 0)} />
                            {/* Part of Distributed, NOT of any spendable balance —
                                these coins are earned but still held until their
                                order's procurement completes. */}
                            <StatCard title="Locked Coins" value={(stats.total_locked_coins ?? 0).toLocaleString('en-IN')} />
                        </StatCardRow>
                    )}
                </div>

                {/* Active Filter Chips */}
                {(fromDate || toDate || role) && (
                    <div className="flex flex-wrap items-center gap-2 mb-6 px-1">
                        {role && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-bold border border-indigo-100/50 shadow-sm transition-all hover:shadow hover:bg-indigo-100">
                                <span className="opacity-75 font-medium">Role:</span>
                                {role === 'SpecialCategory' ? 'Special Category' : role === 'SuperAdmin' ? 'Super Admin' : role}
                                <button onClick={() => { setRole(''); setCurrentPage(1); }} className="p-0.5 hover:bg-indigo-200/50 rounded-full transition-colors ml-0.5"><X size={12} /></button>
                            </span>
                        )}
                        {fromDate && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-bold border border-indigo-100/50 shadow-sm transition-all hover:shadow hover:bg-indigo-100">
                                <span className="opacity-75 font-medium">From:</span>
                                {formatDateToDdmmyyyy(fromDate)}
                                <button onClick={() => { setFromDate(''); setCurrentPage(1); }} className="p-0.5 hover:bg-indigo-200/50 rounded-full transition-colors ml-0.5"><X size={12} /></button>
                            </span>
                        )}
                        {toDate && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-bold border border-indigo-100/50 shadow-sm transition-all hover:shadow hover:bg-indigo-100">
                                <span className="opacity-75 font-medium">To:</span>
                                {formatDateToDdmmyyyy(toDate)}
                                <button onClick={() => { setToDate(''); setCurrentPage(1); }} className="p-0.5 hover:bg-indigo-200/50 rounded-full transition-colors ml-0.5"><X size={12} /></button>
                            </span>
                        )}
                        <button
                            onClick={() => { setRole(''); setFromDate(''); setToDate(''); setCurrentPage(1); }}
                            className="text-[11px] font-bold text-slate-400 hover:text-slate-600 transition-colors ml-2 underline decoration-slate-300 underline-offset-2"
                        >
                            Clear All Filters
                        </button>
                    </div>
                )}

                {/* Table Container */}
                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                        <table className="w-full border-collapse">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-slate-50 border-b-2 border-slate-200">
                                    <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center">S.No</th>
                                    <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center">Name / Mobile</th>
                                    <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center">Role</th>
                                    <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center">Status</th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center cursor-pointer group select-none" onClick={() => handleSort('referrals')}>
                                        <div className="flex items-center justify-center gap-1">
                                            Referrals
                                            <SortIcon sortKey="referrals" />
                                        </div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center cursor-pointer group select-none" onClick={() => handleSort('units')}>
                                        <div className="flex items-center justify-center gap-1">
                                            Units
                                            <SortIcon sortKey="units" />
                                        </div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center cursor-pointer group select-none" onClick={() => handleSort('coinsEarned')}>
                                        <div className="flex items-center justify-center gap-1">
                                            Coins Earned
                                            <SortIcon sortKey="coinsEarned" />
                                        </div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center">Redeemed</th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center">Remaining</th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center">Locked</th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center">Direct Locked</th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center">Indirect Locked</th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center cursor-pointer group select-none" onClick={() => handleSort('joinedDate')}>
                                        <div className="flex items-center justify-center gap-1">
                                            Joined Date
                                            <SortIcon sortKey="joinedDate" />
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <TableSkeleton cols={11} rows={10} />
                                ) : users.length === 0 ? (
                                    <tr>
                                        <td colSpan={13} className="px-4 py-16 text-center text-slate-400 font-medium">No users found</td>
                                    </tr>
                                ) : (
                                    sortedUsers.map((user: any, index: number) => (
                                        <tr
                                            key={user.mobile || index}
                                            className="hover:bg-slate-50/80 transition-colors border-b border-slate-100 last:border-0 cursor-pointer"
                                            onClick={() => navigate(`/user-management/network/${user.mobile}`)}
                                        >
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center">
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center">
                                                <div className="flex items-center justify-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-[#F1F5F9] flex items-center justify-center overflow-hidden border border-[#E2E8F0]">
                                                        <Users size={16} className="text-[#94A3B8]" />
                                                    </div>
                                                    <div className="flex flex-col text-left">
                                                        <span className="font-semibold text-[#1E293B] text-[0.8125rem] whitespace-nowrap">
                                                            {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || '-'}
                                                        </span>
                                                        {user.mobile ? (
                                                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                                <span className="text-[12px] text-slate-600 whitespace-nowrap cursor-pointer hover:text-[#1B4332] font-semibold" onClick={() => navigate(`/user-management/network/${user.mobile}`)}>
                                                                    {revealedFields.has(`mobile-${user.mobile}`) ? `+91 ${String(user.mobile)}` : maskMobile(String(user.mobile))}
                                                                </span>
                                                                <button
                                                                    onClick={(e) => toggleReveal(`mobile-${user.mobile}`, e)}
                                                                    className="p-0.5 rounded hover:bg-slate-100"
                                                                    title={revealedFields.has(`mobile-${user.mobile}`) ? 'Hide' : 'Show'}
                                                                >
                                                                    {revealedFields.has(`mobile-${user.mobile}`)
                                                                        ? <EyeOff size={11} className="text-slate-400" />
                                                                        : <Eye size={11} className="text-slate-400" />}
                                                                </button>
                                                                <button
                                                                    onClick={(e) => handleCopy(String(user.mobile), `mobile-${user.mobile}`, e)}
                                                                    className="p-0.5 rounded hover:bg-slate-100"
                                                                    title="Copy mobile"
                                                                >
                                                                    {copiedField === `mobile-${user.mobile}`
                                                                        ? <Check size={11} className="text-green-500" />
                                                                        : <Copy size={11} className="text-slate-400" />}
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span className="text-slate-400 text-[12px]">—</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center">
                                                <span className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${user.role === 'Investor' ? 'bg-[#FFF8E6] text-[#D97706]' :
                                                    user.role === 'Employee' ? 'bg-[#EBF2FF] text-[#2563EB]' :
                                                        user.role === 'Supervisor' ? 'bg-[#ECFDF5] text-[#059669]' :
                                                        user.role === 'Cashier' ? 'bg-[#FFF1F2] text-[#E11D48]' :
                                                        user.role === 'SpecialCategory' ? 'bg-[#F3E8FF] text-[#9333EA]' :
                                                            user.role === 'SuperAdmin' ? 'bg-[#FEE2E2] text-[#DC2626]' :
                                                                user.role === 'Admin' ? 'bg-[#E0F2FE] text-[#0284C7]' :
                                                                    'bg-[#F1F5F9] text-[#475569]'
                                                    }`}>
                                                    {user.role === 'SuperAdmin' ? 'Super Admin' : user.role}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-center">
                                                <ActiveStatusBadge user={user} />
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center font-semibold">
                                                {user.referral_count}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center">
                                                {user.units_purchased || 0}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#FFF8E6] text-[#D97706]">
                                                    {user.total_coins?.toLocaleString('en-IN')}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#FFF1F2] text-[#E11D48]">
                                                    {(user.total_redeemed_coins ?? 0).toLocaleString('en-IN')}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#ECFDF5] text-[#059669]">
                                                    {(user.total_remaining_coins ?? 0).toLocaleString('en-IN')}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center">
                                                {(user.locked_coins ?? 0) > 0 ? (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#FEF3C7] text-[#B45309]">
                                                        {(user.locked_coins ?? 0).toLocaleString('en-IN')}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-300">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center">
                                                {(user.direct_locked_coins ?? 0) > 0 ? (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700">
                                                        {(user.direct_locked_coins ?? 0).toLocaleString('en-IN')}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-300">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center">
                                                {(user.indirect_locked_coins ?? 0) > 0 ? (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700">
                                                        {(user.indirect_locked_coins ?? 0).toLocaleString('en-IN')}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-300">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-center whitespace-nowrap">
                                                {formatDateToDdmmyyyy(user.created_date)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                </div>

                {/* Pagination */}
                {/* Pagination */}
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages || 1}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                        document.getElementById('dashboard-main-scroll-container')?.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                />
            </div>
        </div>
    );
};

export default NetworkTab;
